import {
  ConfigurationProvider as MuiAdminConfigurationProvider,
  NavigationContextProvider,
} from '@kirz/mui-admin';
import React, { useContext, useEffect } from 'react';
import {
  Routes as DomRoutes,
  Route,
  useNavigate,
  Navigate,
} from 'react-router-dom';

import { RouteGuard } from 'components/RouteGuard';
import { SplashLoader } from 'components/SplashLoader';
import { Configuration } from 'constants/configuration';
import { UserContext } from 'contexts/UserContext';
import { useSettings } from 'hooks/useSettings';
import { DefaultLayout } from 'layouts/DefaultLayout';
import { ClientForms } from 'pages/ClientForms';
import { Clients } from 'pages/Clients';
import { Companies } from 'pages/Companies';
import { Directories } from 'pages/Directories';
import { Documents } from 'pages/Documents';
import { Login } from 'pages/Login';
import { Logs } from 'pages/Logs';
import { PersonalUploads } from 'pages/PersonalUploads';
import { Suppliers } from 'pages/Suppliers';
import { Uploads } from 'pages/Uploads';
import { Users } from 'pages/Users';
import { WebForm } from 'pages/WebForm';
import { WebForms } from 'pages/WebForms';
import { WebFormLayout } from 'pages/forms/WebFormLayout';
import { getRedirectTo } from 'utils/getRedirectTo';

export default function App() {
  const { isUserLoading, user, hasPermission } = useContext(UserContext);
  const { directories } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    if (isUserLoading || user) {
      return;
    }

    navigate(getRedirectTo());
  }, [navigate, isUserLoading, user]);

  if (isUserLoading) {
    return <SplashLoader visible />;
  }

  const routes = (
    user?.role === 'admin'
      ? [
          {
            path: 'clients',
            element: <Clients />,
          },
          {
            path: 'suppliers',
            element: <Suppliers />,
          },
          {
            path: 'directories',
            element: <Directories />,
          },
          {
            path: 'companies',
            element: <Companies />,
          },
          {
            path: 'users',
            element: <Users />,
          },
          {
            path: 'webforms',
            element: <WebForms />,
          },
          {
            path: 'logs',
            element: <Logs />,
          },
        ]
      : [
          {
            path: 'documents/:id',
            element: <Documents />,
          },
          {
            path: 'uploads',
            element: <Uploads />,
          },
          {
            path: 'personal-uploads',
            element: <PersonalUploads />,
          },
        ]
  ) as {
    path: string;
    permission?: string | string[];
    children?: React.ReactNode;
    element?: React.ReactNode;
  }[];

  const defaultRoute =
    user?.role === 'admin'
      ? routes.find(
          (x) =>
            !x.permission ||
            (typeof x.permission === 'string'
              ? hasPermission(x.permission)
              : !!x.permission.find((y) => hasPermission(y))),
        )?.path || '/'
      : `/documents/${directories[0]?.id ?? 'unknown'}`;

  return (
    <MuiAdminConfigurationProvider {...Configuration().muiAdmin}>
      <NavigationContextProvider>
        <DomRoutes>
          <Route path="login" element={<Login />} />
          {user && (
            <>
              <Route path="/" element={<DefaultLayout />}>
                {routes.map((route) => {
                  const content = route.children || (
                    <Route index element={route.element} />
                  );

                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<RouteGuard permission={route.permission} />}
                    >
                      {content}
                    </Route>
                  );
                })}
                {user.role === 'client' && (
                  <>
                    <Route path="/forms" element={<ClientForms />} />
                  </>
                )}
                <Route index element={<Navigate to={defaultRoute} />} />
              </Route>
              <Route path="/forms/:id" element={<DefaultLayout plain />}>
                <Route path="" element={<WebFormLayout />}>
                  <Route path="" element={<WebForm />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to={defaultRoute} />} />
            </>
          )}
        </DomRoutes>
        <SplashLoader visible={isUserLoading} />
      </NavigationContextProvider>
    </MuiAdminConfigurationProvider>
  );
}
